/**
 * Gender / sex-at-birth resolution.
 *
 * Profile gender is a free-text field and has been written by several different
 * forms over time, so the same person can be stored as `M`, `Male`, `MALE`,
 * `male `, `man`, or a longer phrase. Every consumer used to re-implement its
 * own two-value check, which quietly mapped unrecognised spellings to the wrong
 * sex (or dropped whole questionnaire sections). Resolve through here instead.
 *
 * Anything genuinely ambiguous — `Other`, `O`, non-binary, trans identities,
 * "prefer not to say", blanks — resolves to `null` (unknown) rather than a
 * guess, so callers can fall back to a better source such as the Thrive Check's
 * own sex-at-birth answer.
 */

export type BiologicalSex = 'male' | 'female';

const MALE_WORDS = new Set([
  'm',
  'male',
  'males',
  'man',
  'men',
  'boy',
  'boys',
  'masculine',
  'amab',
]);

const FEMALE_WORDS = new Set([
  'f',
  'female',
  'females',
  'woman',
  'women',
  'girl',
  'girls',
  'feminine',
  'afab',
]);

/**
 * Words that make a value too ambiguous to resolve. "Transgender male" and
 * "non-binary" describe identity, not sex assigned at birth, so guessing either
 * way would be wrong.
 */
const AMBIGUOUS_WORDS = new Set([
  'trans',
  'transgender',
  'transsexual',
  'nonbinary',
  'binary',
  'enby',
  'nb',
  'intersex',
  'genderqueer',
  'genderfluid',
  'agender',
  'other',
  'o',
  'prefer',
  'undisclosed',
]);

const toComparableWords = (value: string): string[] =>
  value
    // Drop bracketed helper text, e.g. "Male [assigned at birth]".
    .replace(/\[[^\]]*\]/g, ' ')
    // Drop a trailing Likert/score suffix, e.g. "Male - 3".
    .replace(/\s[–-]{1,2}\s*\d+\s*$/, ' ')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);

/**
 * Resolve any stored gender spelling to `male`, `female`, or `null` when the
 * value is missing, ambiguous, or not a recognised form.
 */
export const resolveBiologicalSex = (value?: string | null): BiologicalSex | null => {
  if (typeof value !== 'string') return null;

  const words = toComparableWords(value);
  if (words.length === 0) return null;
  if (words.some((word) => AMBIGUOUS_WORDS.has(word))) return null;

  const hasFemale = words.some((word) => FEMALE_WORDS.has(word));
  const hasMale = words.some((word) => MALE_WORDS.has(word));

  // "male or female" and similar tell us nothing about this person.
  if (hasFemale && hasMale) return null;
  if (hasFemale) return 'female';
  if (hasMale) return 'male';

  // Unspaced variants such as "female(assignedatbirth)". Female is checked
  // first because "female" also ends with "male".
  const collapsed = words.join('');
  if (collapsed.startsWith('female')) return 'female';
  if (collapsed.startsWith('male')) return 'male';

  return null;
};

/** True only when the value definitely resolves to female. */
export const isFemaleSex = (value?: string | null): boolean =>
  resolveBiologicalSex(value) === 'female';

/** True only when the value definitely resolves to male. */
export const isMaleSex = (value?: string | null): boolean => resolveBiologicalSex(value) === 'male';

/**
 * Single-letter code used by partner integrations (Thyrocare, Neurowyzr).
 * `fallback` is returned when the value cannot be resolved — pass the code the
 * caller's API requires.
 */
export const toGenderCode = <TFallback extends 'M' | 'F' | null = null>(
  value?: string | null,
  fallback: TFallback = null as TFallback
): 'M' | 'F' | TFallback => {
  const sex = resolveBiologicalSex(value);
  if (sex === 'male') return 'M';
  if (sex === 'female') return 'F';
  return fallback;
};

/** Display label, or `null` when the value cannot be resolved. */
export const toGenderLabel = (value?: string | null): 'Male' | 'Female' | null => {
  const sex = resolveBiologicalSex(value);
  if (sex === 'male') return 'Male';
  if (sex === 'female') return 'Female';
  return null;
};

/**
 * Label for client objects: `Male` / `Female` when resolvable, otherwise the
 * stored value (e.g. `Other`) so it is not silently rewritten, or `fallback`
 * when blank.
 */
export const toGenderDisplay = (value?: string | null, fallback = 'Not specified'): string =>
  toGenderLabel(value) ?? (value?.trim() || fallback);

/**
 * Value for the `M` / `F` / `O` gender dropdowns used by the profile forms.
 *
 * Checkout writes `MALE`/`FEMALE` to `UserProfile.gender` while the profile
 * forms write `M`/`F`/`O`, so a profile created at checkout used to render as
 * an empty "Select gender..." because no option matched. Resolve through here
 * so any stored spelling selects the right option. Unresolvable-but-present
 * values (e.g. `Other`, `NON_BINARY`) map to `O`; blanks stay blank so the
 * field still reads as unanswered.
 */
export const toGenderSelectValue = (value?: string | null): 'M' | 'F' | 'O' | '' => {
  const code = toGenderCode(value);
  if (code) return code;
  return value?.trim() ? 'O' : '';
};

/**
 * Value to write back after a gender dropdown selection.
 *
 * `stored` is what the API last returned. When the answer did not actually
 * change, write the stored spelling back untouched: the profile forms speak
 * `M`/`F`/`O` while checkout writes `MALE`/`FEMALE`, and backend cycle-phase
 * logic keys off the exact `FEMALE` token — so simply opening and saving a
 * profile must not silently rewrite `FEMALE` to `F`.
 */
export const toGenderWriteValue = (
  selected?: string | null,
  stored?: string | null
): string | undefined => {
  const next = selected?.trim();
  if (!next) return undefined;
  if (stored?.trim() && toGenderSelectValue(stored) === toGenderSelectValue(next)) {
    return stored;
  }
  return next;
};
